import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";

import { Session, localStorageValues } from "./models/session";
import { Registration, 
		RegistrationFields, 
		IndustryData, 
		ExitSurveyPayload, 
		EmailFoundDto } from "./models/registration";

import { environment } from "../environments/environment";
import { flatMap } from "rxjs/operators";

const httpOptions = {
	headers: new HttpHeaders({ "Content-Type": "application/json" })
};
@Injectable({
	providedIn: "root"
})
export class SignupService {

	private sessionsUrl = "/api/sessions";
	private registrationsUrl = "/api/registrations";
	private industriesUrl = "/api/industries";
	private uploadUrl = "/api/logos";
	private exitSurveyUrl = "/api/exitsurvey";
	private peopleUrl = "/api/people";

	private apiUrl: string | null;
	errorNoLocalStorage: BehaviorSubject<boolean> = new BehaviorSubject(false);

	constructor(private http: HttpClient) {
		this.apiUrl = environment.apiUrl;
	}

	// browser allow to disable localStorage, so we check if localStorage operations were successful
	checkForLocalStorage() {
		try {
			localStorage.setItem("test", "passed");
			localStorage.removeItem("test");
		}
		catch(error) {
			// no local storage, let listeners know
			this.errorNoLocalStorage.next(true);
		}
	}

	checkIfEmailAlreadyTaken(email: string): Observable<EmailFoundDto> {
		const url = `${this.createUrl(this.peopleUrl)}?email=${email}`;
		return this.http.get<EmailFoundDto>(url, {});
	}
	
	getStoredValues<localStorageValues>() {
		if (this.errorNoLocalStorage.value) 
			return {};
		return Object.keys(localStorage).reduce(function(obj, key) {
			obj[key] = localStorage.getItem(key);
			return obj
		}, {});
	}

	updateStore(values: localStorageValues) {
		if (this.errorNoLocalStorage.value || !values) 
			return;
		Object.keys(values).forEach((key) => {
			if (values[key]) 
				localStorage.setItem(key, values[key]);
		});
	}

	deleteFromStore(key: string) {
		if (this.errorNoLocalStorage.value || !key) 
			return;
		const storedValues = this.getStoredValues();
		if (storedValues[key]) 
			localStorage.removeItem(key);
	}

	uploadFile(fileData) {
		const data = new FormData();
		if (fileData.data) data.append("data", JSON.stringify(fileData.data));
		data.append("logo", fileData.logo);
		return this.http.post(this.createUrl(this.uploadUrl), data);
	}


	initSession(email: string): Observable<Session> {

		return this.checkIfEmailAlreadyTaken(email).pipe(
			flatMap((emailDto) => {
				if (emailDto.emailInUse) {
					throw new GenericError("EMAIL_ALREADY_EXISTS_IN_PLATFORM", "This business is already signed up");
				} else {
					return this.http.post<Session>(this.createUrl(this.sessionsUrl), {email}, httpOptions);
				}
			}
		));

	}

	verifySession(session: Session): Observable<Session> {
		const queryStringEmail = (session && session.email) ? `email=${encodeURIComponent(session.email)}&` : "";
		return this.http.get<Session>(`${this.createUrl(this.sessionsUrl)}?${queryStringEmail}passcode=${session.passcode}`, httpOptions);
	}

	updateRegistration(registration: Registration): Observable<RegistrationFields> {
		return this.http.put<RegistrationFields>(this.createUrl(this.registrationsUrl), registration, httpOptions);
	}

	getRegistration(session: Session): Observable<{fields: RegistrationFields}> {
		const queryStringEmail = (session && session.email) ? `email=${encodeURIComponent(session.email)}&` : "";
		const queryStringPasscode = (session && session.passcode) ? `passcode=${session.passcode}` : "";
		return this.http.get<{fields: RegistrationFields}>(`${this.createUrl(this.registrationsUrl)}?${queryStringEmail}${queryStringPasscode}`, httpOptions);
	}

	finishRegistration(session: Session): Observable<{fields: RegistrationFields}> {
		const data: Registration =  Object.assign(session, { fields: { registrationInitiated: true} });
		return this.http.put<{fields: RegistrationFields}>(`${this.createUrl(this.registrationsUrl)}`, data, httpOptions);
	}

	getIndustries(): Observable<Array<IndustryData>> {
		return this.http.get<Array<IndustryData>>(`${this.createUrl(this.industriesUrl)}`);
	}

	submitExitSurvey(payload: ExitSurveyPayload): Observable<any> {
		return this.http.post(`${this.createUrl(this.exitSurveyUrl)}`, payload);
	}

	private createUrl(url) {
		if (this.apiUrl) {
			const endsWithSlash = this.apiUrl.endsWith("/");
			const root = endsWithSlash ? this.apiUrl.substring(0, this.apiUrl.length - 1) : this.apiUrl;
			return root + url;
		}
		return url;
	}

}

class GenericError extends Error {
	error = {};
	message: string;

	constructor(error, message) {
		super(message);
		this.name = "GenericError";
		this.error = {"error": error };
	}
}
